import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationStart,
  Router,
} from '@angular/router';

import { Store } from '@ngrx/store';
import {
  selectApiVersion,
  selectAppVersion,
  selectIsAppInitialized,
  selectIsPagesComponentExisting,
} from 'src/store/app/app.state';
import {
  resetPermissions,
  resetSettings,
  resetUser,
  setUserId,
} from 'src/store/user/user.actions';
import { resetEnterprises } from 'src/store/enterprises/enterprises.actions';
import {
  selectCountries,
  selectCurrencies,
  selectSalutations,
} from 'src/store/core-data/core-data.state';
import {
  resetCountries,
  resetCurrencies,
  resetSalutations,
} from 'src/store/core-data/core-data.actions';
import { setApiVersion, setIsAppInitialized } from 'src/store/app/app.actions';
import {
  selectPermissions,
  selectSettings,
  selectUser,
} from 'src/store/user/user.state';
import { selectEnterprisesState } from 'src/store/enterprises/enterprises.state';
import { selectPartnerEnterpriseState } from 'src/store/partner-enterprise/partner-enterprise.state';
import { resetPartnerEnterprise } from 'src/store/partner-enterprise/partner-enterprise.actions';

import { Subscription, firstValueFrom } from 'rxjs';

import { environment } from 'src/environments/environment';

import { Utility } from 'src/services/utility.service';

import { CoreDataService } from 'src/services/core-data.service';
import { LearningSuiteService } from 'src/services/learning-suite.service';
import { TranslationService } from 'lipp-components';
import { ToastService } from 'lipp-components';
import { SignalRService } from 'src/services/signalr.service';
import { SignalRService as NewSignalRService } from 'src/services/new-api/new-signalr.service';

import {
  ApiInfo,
  CoreDataLatestModificationOnUTC,
  EnterprisesLatestModificationOnUTC,
  UserDataLatestModificationOnUTC,
} from 'src/models/ApiInfo';
import { Theme } from 'src/models/Theme';

import { Config } from 'lipp-components';

import de from 'src/i18n/de.json';
import en from 'src/i18n/en.json';

@Component({
  selector: 'app',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  public isAppInitialized$ = this.store.select(selectIsAppInitialized);
  public isPagesComponentExisting$ = this.store.select(
    selectIsPagesComponentExisting,
  );

  public isLoadingRoute: boolean = false;
  private loadingRouteSubscription?: Subscription;

  private settingsSubscription?: Subscription;

  constructor(
    private store: Store,
    private coreDataService: CoreDataService,
    private router: Router,
    private toastService: ToastService,
    private translationService: TranslationService,
    private signalR: SignalRService,
    private newSignalR: NewSignalRService,
  ) {}

  async ngOnInit(): Promise<void> {
    Config.translationFiles$.next({ de, en });

    this.loadingRouteSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.isLoadingRoute = true;
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel
      ) {
        this.isLoadingRoute = false;
      }
    });

    this.settingsSubscription = this.store
      .select(selectSettings)
      .subscribe((settings) => {
        const theme: Theme = settings
          ? Utility.getThemeFromSetting(settings.theme)
          : 'light';

        if (theme === 'light' && document.body.classList.contains('dark')) {
          document.body.classList.remove('dark');
        } else if (
          theme === 'dark' &&
          !document.body.classList.contains('dark')
        ) {
          document.body.classList.add('dark');
        }

        Config.theme$.next(theme);
      });

    const appVersion: string = await firstValueFrom(
      this.store.select(selectAppVersion),
    );

    const environmentName: string = environment.development
      ? 'Development'
      : environment.staging
        ? 'Staging'
        : 'Production';

    console.info('%c' + 'Environment', 'font-weight: bold', environmentName);
    console.info('%c' + 'Lipp Portal', 'font-weight: bold', appVersion);

    this.signalR.startConnection();
    this.newSignalR.start();

    const quickLinkToken: string | undefined =
      Utility.getQueryParams()['quicklink'];

    if (quickLinkToken) {
      Utility.setStore('token', quickLinkToken, false);
    }

    // SSO-Anfrage (LearningSuite) bzw. Zustimmung einer KI-Anwendung (LIPP-1500) vor der
    // Token-Prüfung merken: Bei abgelaufener Sitzung leitet loadApiInfo auf den Login, bevor die
    // Seite überhaupt geladen wird.
    if (
      window.location.pathname === '/sso/authorize' ||
      window.location.pathname === '/oauth/consent'
    ) {
      LearningSuiteService.rememberPendingSsoAuthorize(
        window.location.search,
        window.location.pathname
      );
    }

    await this.loadApiInfo();

    this.store.dispatch(setIsAppInitialized({ isAppInitialized: true }));
  }

  ngOnDestroy(): void {
    if (this.loadingRouteSubscription) {
      this.loadingRouteSubscription.unsubscribe();
    }

    if (this.settingsSubscription) {
      this.settingsSubscription.unsubscribe();
    }

    this.signalR.closeConnection();
  }

  private async loadApiInfo(): Promise<boolean> {
    const apiInfoResult = await firstValueFrom(
      this.coreDataService.getApiInfo(),
    );

    if (!apiInfoResult.ok) {
      this.toastService.showAlert(
        this.translationService.translate('FatalError.LoadingApiInfo'),
        this.translationService.translate(apiInfoResult.errors![0].key),
      );

      this.router.navigateByUrl('/error');
      return false;
    }

    const apiInfo: ApiInfo = apiInfoResult.value!;
    const oldApiVersion = await firstValueFrom(
      this.store.select(selectApiVersion),
    );

    this.store.dispatch(setApiVersion({ apiVersion: apiInfo.version }));
    this.store.dispatch(setUserId({ userId: apiInfo.userId }));

    const splitApiVersion: string[] = apiInfo.version.split('.');
    const apiVersionWithoutBuildNumber: string = `${splitApiVersion[0]}.${splitApiVersion[1]}.${splitApiVersion[2]}`;

    console.info(
      '%c' + 'API',
      'font-weight: bold',
      apiVersionWithoutBuildNumber,
    );

    if (
      apiInfo.inMaintenance &&
      !(Utility.getQueryParams()['ignoreMaintenance'] == 'true')
    ) {
      this.router.navigateByUrl('/maintenance');
      return true;
    }

    if (!oldApiVersion || apiInfo.version != oldApiVersion) {
      this.store.dispatch(resetUser());
      this.store.dispatch(resetSettings());
      this.store.dispatch(resetPermissions());

      this.store.dispatch(resetEnterprises());
      this.store.dispatch(resetPartnerEnterprise());

      return true;
    }

    const user = await firstValueFrom(this.store.select(selectUser));

    if (user?.id !== apiInfo.userId) {
      this.store.dispatch(resetUser());
      this.store.dispatch(resetSettings());
      this.store.dispatch(resetPermissions());

      this.store.dispatch(resetEnterprises());
      this.store.dispatch(resetPartnerEnterprise());
    }

    await this.deleteOutdatedCoreData(apiInfo.latestModificationOnUTC.coreData);

    switch (apiInfo.isTokenValid) {
      case true:
        await this.deleteOutdatedUserData(
          apiInfo.latestModificationOnUTC.userData!,
        );
        await this.deleteOutdatedEnterprises(
          apiInfo.latestModificationOnUTC.enterprises!,
        );
        break;

      case false:
        this.toastService.showInfo(
          this.translationService.translate('Authentication.SessionExpired'),
        );

        Utility.removeStore('token');
        this.router.navigateByUrl('/auth/login');
        break;

      case undefined:
        break;
    }

    return true;
  }

  private async deleteOutdatedCoreData(
    coreDataLatestModificationOnUTC: CoreDataLatestModificationOnUTC,
  ): Promise<void> {
    const countries = await firstValueFrom(this.store.select(selectCountries));

    if (
      countries &&
      countries.latestModificationOnUTC.getTime() <
        coreDataLatestModificationOnUTC.countries.getTime()
    ) {
      this.store.dispatch(resetCountries());
    }

    const currencies = await firstValueFrom(
      this.store.select(selectCurrencies),
    );

    if (
      currencies &&
      currencies.latestModificationOnUTC.getTime() <
        coreDataLatestModificationOnUTC.currencies.getTime()
    ) {
      this.store.dispatch(resetCurrencies());
    }

    const salutations = await firstValueFrom(
      this.store.select(selectSalutations),
    );

    if (
      salutations &&
      salutations.latestModificationOnUTC.getTime() <
        coreDataLatestModificationOnUTC.salutations.getTime()
    ) {
      this.store.dispatch(resetSalutations());
    }
  }

  private async deleteOutdatedUserData(
    userDataLatestModificationOnUTC: UserDataLatestModificationOnUTC,
  ): Promise<void> {
    const user = await firstValueFrom(this.store.select(selectUser));

    if (
      user &&
      user.latestModificationOnUTC.getTime() <
        userDataLatestModificationOnUTC.user.getTime()
    ) {
      this.store.dispatch(resetUser());
    }

    const settings = await firstValueFrom(this.store.select(selectSettings));

    if (
      settings &&
      settings.latestModificationOnUTC.getTime() <
        userDataLatestModificationOnUTC.settings.getTime()
    ) {
      this.store.dispatch(resetSettings());
    }

    const permissions = await firstValueFrom(
      this.store.select(selectPermissions),
    );

    // Todo: not working correctly
    // if (
    //   permissions &&
    //   permissions.latestModificationOnUTC.getTime() <
    //     userDataLatestModificationOnUTC.permissions.getTime()
    // ) {
    //   this.store.dispatch(resetPermissions());
    // }

    this.store.dispatch(resetPermissions());
  }

  private async deleteOutdatedEnterprises(
    enterprisesLatestModificationOnUTC: EnterprisesLatestModificationOnUTC,
  ): Promise<void> {
    this.store.dispatch(resetEnterprises());
    this.store.dispatch(resetPartnerEnterprise());
    return;

    // if (enterprisesLatestModificationOnUTC.selectedEnterprise) {
    //   const settings = await firstValueFrom(this.store.select(selectSettings));

    //   if (!settings) {
    //     this.store.dispatch(resetEnterprises());
    //   } else {
    //     const enterprisesState = await firstValueFrom(
    //       this.store.select(selectEnterprisesState)
    //     );

    //     if (enterprisesState.enterprises) {
    //       if (enterprisesState.enterprises.length != 1) {
    //         this.store.dispatch(resetEnterprises());
    //       } else if (
    //         enterprisesState.latestModificationOnUTC!.getTime() <
    //         enterprisesLatestModificationOnUTC.selectedEnterprise.getTime()
    //       ) {
    //         this.store.dispatch(resetEnterprises());
    //       }
    //     }
    //   }
    // } else if (enterprisesLatestModificationOnUTC.enterprises) {
    //   const enterprisesState = await firstValueFrom(
    //     this.store.select(selectEnterprisesState)
    //   );

    //   if (
    //     enterprisesState.enterprises &&
    //     enterprisesState.latestModificationOnUTC!.getTime() <
    //       enterprisesLatestModificationOnUTC.enterprises.getTime()
    //   ) {
    //     this.store.dispatch(resetEnterprises());
    //   }
    // } else {
    //   this.store.dispatch(resetEnterprises());
    // }

    // if (enterprisesLatestModificationOnUTC.partnerEnterprise) {
    //   const partnerEnterpriseState = await firstValueFrom(
    //     this.store.select(selectPartnerEnterpriseState)
    //   );

    //   if (
    //     partnerEnterpriseState.partnerEnterprise &&
    //     partnerEnterpriseState.latestModificationOnUTC!.getTime() <
    //       enterprisesLatestModificationOnUTC.partnerEnterprise.getTime()
    //   ) {
    //     this.store.dispatch(resetPartnerEnterprise());
    //   }
    // } else {
    //   this.store.dispatch(resetPartnerEnterprise());
    // }
  }
}
